// Darius Apprehend — a massive crimson axe head sweeps in an arc toward the target.
// Three blood droplets trail behind. Impact crater of blood.

import type { Animation } from "../../engine/animation/AnimationQueue";
import { Easing } from "../../engine/animation/Easing";
import type { Point } from "../../engine/hex/HexLayout";

const SWING_MS  = 320;
const SPLAT_MS  = 240;
const TOTAL     = SWING_MS + SPLAT_MS;

const AXE_W     = 34;   // blade half-width at widest
const AXE_H     = 22;   // blade height
const HANDLE_L  = 42;   // handle length

export class DariusAxeSwingVFX implements Animation {
  private elapsed = 0;
  private angle: number;
  private dist: number;

  constructor(
    private from: Point,
    private to: Point,
    private onImpact?: () => void,
  ) {
    const dx = to.x - from.x;
    const dy = to.y - from.y;
    this.angle = Math.atan2(dy, dx);
    this.dist  = Math.sqrt(dx * dx + dy * dy);
  }

  update(dt: number): void {
    const prev = this.elapsed;
    this.elapsed = Math.min(this.elapsed + dt, TOTAL);
    if (prev < SWING_MS && this.elapsed >= SWING_MS) this.onImpact?.();
  }

  draw(ctx: CanvasRenderingContext2D): void {
    if (this.elapsed < SWING_MS) {
      this.drawSwing(ctx, this.elapsed / SWING_MS);
    } else {
      this.drawSplat(ctx, (this.elapsed - SWING_MS) / SPLAT_MS);
    }
  }

  private drawSwing(ctx: CanvasRenderingContext2D, t: number): void {
    const eased  = Easing.easeInQuad(t);
    const headX  = this.from.x + (this.to.x - this.from.x) * eased;
    const headY  = this.from.y + (this.to.y - this.from.y) * eased;

    // Arc rotation: starts 0.9 radians above travel angle, lands at travel angle
    const swingAngle = this.angle - 0.9 * (1 - eased);

    ctx.save();
    ctx.translate(headX, headY);
    ctx.rotate(swingAngle);

    // Whoosh trail (fading arc)
    const trailT  = Math.max(0, eased - 0.15);
    const trailX0 = (this.from.x + (this.to.x - this.from.x) * trailT) - headX;
    const trailY0 = (this.from.y + (this.to.y - this.from.y) * trailT) - headY;
    ctx.globalAlpha = (1 - eased) * 0.4;
    ctx.strokeStyle = "#aa1111";
    ctx.lineWidth   = AXE_H * 1.2;
    ctx.lineCap     = "round";
    ctx.shadowColor = "#880000";
    ctx.shadowBlur  = 12;
    ctx.beginPath();
    ctx.moveTo(trailX0, trailY0);
    ctx.lineTo(0, 0);
    ctx.stroke();

    ctx.globalAlpha = 1;

    // Handle
    ctx.shadowColor = "#330000";
    ctx.shadowBlur  = 8;
    ctx.fillStyle   = "#3a1a08";
    ctx.fillRect(-HANDLE_L - 4, -4, HANDLE_L + 4, 8);

    // Axe blade — crescent shape (two arcs)
    ctx.shadowColor = "#cc2200";
    ctx.shadowBlur  = 18;
    const bladeGrad = ctx.createLinearGradient(-AXE_W, -AXE_H, AXE_W * 0.3, AXE_H);
    bladeGrad.addColorStop(0, "#cc2200");
    bladeGrad.addColorStop(0.45, "#ff4422");
    bladeGrad.addColorStop(0.75, "#cc1100");
    bladeGrad.addColorStop(1, "#660000");
    ctx.fillStyle = bladeGrad;

    ctx.beginPath();
    // Outer blade edge
    ctx.arc(0, 0, AXE_W, -Math.PI * 0.55, Math.PI * 0.55, false);
    // Inner curve back
    ctx.arc(AXE_W * 0.3, 0, AXE_W * 0.55, Math.PI * 0.58, -Math.PI * 0.58, true);
    ctx.closePath();
    ctx.fill();

    // Blade edge highlight
    ctx.globalAlpha = 0.75;
    ctx.strokeStyle = "#ffbbaa";
    ctx.lineWidth   = 2;
    ctx.shadowColor = "#ff6644";
    ctx.shadowBlur  = 12;
    ctx.beginPath();
    ctx.arc(0, 0, AXE_W, -Math.PI * 0.55, Math.PI * 0.55, false);
    ctx.stroke();

    // Blood on blade (dark red streaks)
    ctx.globalAlpha = 0.5;
    ctx.fillStyle   = "#880000";
    ctx.shadowBlur  = 0;
    for (let i = -1; i <= 1; i++) {
      ctx.beginPath();
      ctx.ellipse(AXE_W * 0.4, i * AXE_H * 0.25, 5, 3, Math.PI * 0.3, 0, Math.PI * 2);
      ctx.fill();
    }

    // Blood droplets trailing
    for (let i = 0; i < 3; i++) {
      const dropT = Math.max(0, eased - i * 0.15);
      if (dropT <= 0) continue;
      const dropDist = -(HANDLE_L + 12 + i * 20) - dropT * 18;
      const dropY    = (i - 1) * 7 + Math.sin(dropT * Math.PI) * 8;
      ctx.globalAlpha = (1 - dropT) * 0.75;
      ctx.fillStyle   = "#cc1100";
      ctx.shadowColor = "#880000";
      ctx.shadowBlur  = 6;
      ctx.beginPath();
      ctx.ellipse(dropDist, dropY, 3 + (2 - i), 4, 0.3, 0, Math.PI * 2);
      ctx.fill();
    }

    ctx.restore();
  }

  private drawSplat(ctx: CanvasRenderingContext2D, t: number): void {
    const fade = 1 - Easing.easeInQuad(t);
    const x = this.to.x;
    const y = this.to.y;

    ctx.save();

    // Impact ring
    ctx.globalAlpha = fade * 0.7;
    ctx.strokeStyle = "#cc2200";
    ctx.lineWidth   = 2.5;
    ctx.shadowColor = "#880000";
    ctx.shadowBlur  = 14;
    ctx.beginPath();
    ctx.arc(x, y, 8 + t * 30, 0, Math.PI * 2);
    ctx.stroke();

    // Blood splatter blobs
    const SPLAT_PTS = [[1.2, 0.4], [2.3, 1.1], [0.5, 2.0], [-1.0, 1.8], [-1.7, 0.7],
                       [0.2, -1.5], [1.5, -0.9], [-0.8, -1.2]];
    for (const [ax, ay] of SPLAT_PTS) {
      const sx = x + ax * (6 + t * 20);
      const sy = y + ay * (6 + t * 20);
      ctx.globalAlpha = fade * 0.65;
      ctx.fillStyle   = "#aa1100";
      ctx.shadowBlur  = 4;
      ctx.beginPath();
      ctx.arc(sx, sy, 2.5, 0, Math.PI * 2);
      ctx.fill();
    }

    ctx.restore();
  }

  isDone(): boolean {
    return this.elapsed >= TOTAL;
  }
}
